import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  TableContainer,
  styled,
  tableCellClasses,
  tableRowClasses,
  ThemeProvider,
  Button,
  Box,
  Stack,
  Dialog,
} from '@mui/material';
import { PaymentEstimateObject } from 'api/tax-delivery';
import { MinusIcon, PlusIcon } from 'common/Icons';
import {
  useDeletePaymentEstimates as useDeletePaymentEstimate,
  useGetPaymentEstimates,
} from 'hooks/payment-hooks';
import { SyntheticEvent, useState } from 'react';
import liscioLightTheme from 'theme/liscioLight';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Add } from '@mui/icons-material';
import { PaymentEstimates } from 'components/PaymentEstimates/PaymentEstimates';
import { ConfirmationDialog } from 'ui/ConfirmationDialog/ConfirmationDialog';
import TaxDetailsDialog from './components/TaxDetailsDialog/TaxDetailsDialog';
import { TaxInCharge } from './TaxInCharge';
import { K1Packages } from './components/K1Packages/K1Packages';

interface Props {
  accountId: string;
  legacyAccountId: number;
  taxInChargeId: number;
  isExpanded: (panel: string) => boolean;
  handleChange: (
    panel: string,
  ) => (event: SyntheticEvent, expanded: boolean) => void;
}

export const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  [`& .${tableRowClasses.root}`]: {},
  [`& .${tableCellClasses.root}`]: {
    [`&.${tableCellClasses.head}`]: {
      ...theme.typography.body2,
      fontWeight: 700,
    },
    [`&.${tableCellClasses.body}`]: {
      fontWeight: 400,
      ...theme.typography.body1,
      letterSpacing: 'inherit',
      textTransform: 'none',
      p: {
        maxWidth: '15rem',
      },
    },
  },
}));

export default function TaxDetailsAccordion({
  isExpanded,
  handleChange,
  accountId,
  legacyAccountId,
  taxInChargeId,
}: Props) {
  const { taxDetails } = useFlags();
  const [selectedData, setSelectedData] = useState<PaymentEstimateObject>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<
    PaymentEstimateObject | undefined
  >();
  const {
    data: paymentEstimates,
    refetch,
    isInitialLoading,
  } = useGetPaymentEstimates(accountId);
  const { mutate: deletePaymentMutation } = useDeletePaymentEstimate();
  const [DialogOpen, setDialogOpen] = useState(false);

  const handleAdd = () => {
    setSelectedData(undefined);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    refetch();
    setDialogOpen(false);
  };

  const handleDelete = (id?: string) => {
    if (!id) {
      return;
    }

    const payEstimate = paymentEstimates?.find(
      (estimate) => estimate.uuid === id,
    );

    setShowDeleteConfirmation(payEstimate);
  };

  const handleDeleteConfirmation = () => {
    if (showDeleteConfirmation && showDeleteConfirmation?.uuid) {
      deletePaymentMutation(showDeleteConfirmation.uuid, {
        onSettled: () => {
          setShowDeleteConfirmation(undefined);
          refetch();
        },
      });
    }
  };

  const handleEdit = (data: PaymentEstimateObject) => {
    setSelectedData(data);
    setDialogOpen(true);
  };

  if (!taxDetails) {
    return null;
  }

  if (isInitialLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      expanded={isExpanded('tax_details')}
      onChange={handleChange('tax_details')}
    >
      <AccordionSummary
        id="tax_details-header"
        expandIcon={isExpanded('tax_details') ? <MinusIcon /> : <PlusIcon />}
        aria-controls="tax_details-content"
      >
        <Typography fontWeight="bolder" variant="h6">
          Tax Details
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <ThemeProvider theme={liscioLightTheme}>
          <Stack>
            <TaxInCharge
              legacyAccountId={legacyAccountId}
              taxInChargeId={taxInChargeId}
            />
            <Box id="datepicker-portal" sx={{ zIndex: 9999 }} />
            <Box marginLeft="auto">
              <Button
                startIcon={<Add />}
                onClick={handleAdd}
                variant="contained"
              >
                Tax Jurisdiction
              </Button>
            </Box>
            <Typography variant="h3" pb={2.5}>
              Quarterly Payments
            </Typography>
            <PaymentEstimates
              paymentEstimates={paymentEstimates}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
            <K1Packages accountUuid={accountId} />
            <Dialog open={DialogOpen} maxWidth="lg">
              <Box>
                <TaxDetailsDialog
                  accountId={accountId}
                  estimate={selectedData}
                  paymentEstimates={paymentEstimates}
                  onSuccess={handleSuccess}
                  onCancel={() => setDialogOpen(false)}
                />
              </Box>
            </Dialog>
            {showDeleteConfirmation && (
              <ConfirmationDialog
                confirmationText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteConfirmation}
                onCancel={() => setShowDeleteConfirmation(undefined)}
              >
                <Typography>{`Are you sure you want to delete all ${showDeleteConfirmation?.jurisdiction} Estimated Payments for ${showDeleteConfirmation?.tax_year}?`}</Typography>
              </ConfirmationDialog>
            )}
          </Stack>
        </ThemeProvider>
      </AccordionDetails>
    </Accordion>
  );
}
